import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiMail, FiLock, FiUser, FiTruck, FiBox, FiMapPin, FiEye, FiEyeOff, FiDollarSign, FiPercent, FiGift, FiInfo } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { LogoWhite } from '../../components/common/Logo';
import api from '../../services/api';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'cliente',
    whatsapp: '',
    cpf: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [location, setLocation] = useState(null);
  const [fees, setFees] = useState(null);
  const [showFeesInfo, setShowFeesInfo] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Carregar taxas quando selecionar lojista
  useEffect(() => {
    if (formData.userType === 'lojista' && !fees) {
      loadFees();
    }
  }, [formData.userType]);

  const loadFees = async () => {
    try {
      const response = await api.get('/config/fees');
      setFees(response.data);
    } catch (error) {
      console.error('Erro ao carregar taxas:', error);
    }
  };

  const userTypes = [
    { value: 'cliente', label: 'Cliente', icon: FiUser, description: 'Compre eletrônicos' },
    { value: 'lojista', label: 'Lojista', icon: FiBox, description: 'Venda seus produtos' },
    { value: 'entregador', label: 'Entregador', icon: FiTruck, description: 'Faça entregas' }
  ];

  const getLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setGettingLocation(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setGettingLocation(false);
          // Fallback para Barueri
          setLocation({ lat: -23.5114, lng: -46.8763 });
        }
      );
    } else {
      setGettingLocation(false);
      setLocation({ lat: -23.5114, lng: -46.8763 });
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push("Mínimo 8 caracteres");
    if (!/[A-Z]/.test(password)) errors.push("Uma letra maiúscula");
    if (!/[a-z]/.test(password)) errors.push("Uma letra minúscula");
    if (!/\d/.test(password)) errors.push("Um número");
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push("Um caractere especial (!@#$%^&*)");
    return errors;
  };

  const [passwordErrors, setPasswordErrors] = useState([]);

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({...formData, password: newPassword});
    setPasswordErrors(validatePassword(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate password strength
    const pwdErrors = validatePassword(formData.password);
    if (pwdErrors.length > 0) {
      setError('A senha deve conter: ' + pwdErrors.join(', '));
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      setLoading(false);
      return;
    }

    // Validar CPF para lojistas
    if (formData.userType === 'lojista') {
      if (!formData.cpf || formData.cpf.replace(/\D/g, '').length !== 11) {
        setError('CPF inválido. Deve ter 11 dígitos.');
        setLoading(false);
        return;
      }
    }

    try {
      const user = await register(
        formData.name,
        formData.email,
        formData.password,
        formData.userType,
        location,
        formData.whatsapp || null,
        formData.userType === 'lojista' ? formData.cpf : null
      );
      
      switch (user.user_type) {
        case 'cliente':
          navigate('/cliente');
          break;
        case 'lojista':
          navigate('/lojista');
          break;
        case 'entregador':
          navigate('/entregador');
          break;
        default:
          navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A1A1A] via-[#2D2D2D] to-[#DC143C] flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <LogoWhite size="lg" showText={true} />
          </div>
          <p className="text-white/70 mt-2">Crie sua conta</p>
        </div>

        {/* Register Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-[#1A1A1A] mb-6">Cadastrar</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* User Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Conta</label>
              <div className="grid grid-cols-3 gap-2">
                {userTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, userType: type.value })}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.userType === type.value
                        ? 'border-[#DC143C] bg-red-50 text-[#DC143C]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <type.icon className="mx-auto text-xl mb-1" />
                    <span className="text-xs font-medium block">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Taxas e Cobranças para Lojista */}
            {formData.userType === 'lojista' && fees && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <FiInfo className="text-blue-600" />
                    Taxas e Cobranças
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowFeesInfo(!showFeesInfo)}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    {showFeesInfo ? 'Ocultar detalhes' : 'Ver detalhes'}
                  </button>
                </div>

                {/* Primeiro Mês Grátis */}
                {fees.first_month_free && (
                  <div className="bg-green-100 border border-green-300 rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-2 text-green-800">
                      <FiGift className="text-lg" />
                      <span className="font-semibold">🎉 Primeiro mês GRÁTIS!</span>
                    </div>
                    <p className="text-xs text-green-700 mt-1">
                      Aproveite! A mensalidade só começa após 30 dias do cadastro da sua loja.
                    </p>
                  </div>
                )}

                {/* Resumo das Taxas */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-blue-200">
                    <span className="text-gray-600">Mensalidade</span>
                    <span className="font-bold text-gray-800">
                      R$ {fees.monthly_fee?.toFixed(2).replace('.', ',')}/mês
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-blue-200">
                    <span className="text-gray-600">Taxa de Entrega</span>
                    <span className="font-medium text-gray-800">
                      R$ {fees.delivery_fee?.total?.toFixed(2).replace('.', ',')} <span className="text-xs text-gray-500">(cliente paga)</span>
                    </span>
                  </div>
                </div>

                {/* Detalhes das Comissões */}
                {showFeesInfo && (
                  <div className="mt-4 pt-3 border-t border-blue-200">
                    <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FiPercent className="text-blue-600" />
                      Comissões por Venda (valor fixo)
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center bg-white/60 rounded-lg px-3 py-2">
                        <span className="text-gray-600">{fees.commissions?.low?.description}</span>
                        <span className="font-semibold text-green-600">R$ {fees.commissions?.low?.fee?.toFixed(2).replace('.', ',')}</span>
                      </div>
                      <div className="flex justify-between items-center bg-white/60 rounded-lg px-3 py-2">
                        <span className="text-gray-600">{fees.commissions?.medium?.description}</span>
                        <span className="font-semibold text-yellow-600">R$ {fees.commissions?.medium?.fee?.toFixed(2).replace('.', ',')}</span>
                      </div>
                      <div className="flex justify-between items-center bg-white/60 rounded-lg px-3 py-2">
                        <span className="text-gray-600">{fees.commissions?.high?.description}</span>
                        <span className="font-semibold text-red-600">R$ {fees.commissions?.high?.fee?.toFixed(2).replace('.', ',')}</span>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-3 italic">
                      * A comissão é cobrada apenas quando a venda é concluída. 
                      Quanto maior o valor da venda, maior a comissão fixa.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none transition-all"
                  placeholder="Seu nome"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none transition-all"
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">📱</span>
                <input
                  type="tel"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none transition-all"
                  placeholder="(11) 99999-9999"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {formData.userType === 'lojista' 
                  ? 'Usado pela plataforma para contato. Não será exibido para clientes.'
                  : 'Para comunicação sobre pedidos e entregas.'}
              </p>
            </div>

            {/* CPF obrigatório para lojistas */}
            {formData.userType === 'lojista' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🪪</span>
                  <input
                    type="text"
                    value={formData.cpf}
                    onChange={(e) => {
                      // Formatar CPF automaticamente
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length > 11) value = value.slice(0, 11);
                      if (value.length > 9) {
                        value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
                      } else if (value.length > 6) {
                        value = value.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
                      } else if (value.length > 3) {
                        value = value.replace(/(\d{3})(\d{1,3})/, '$1.$2');
                      }
                      setFormData({ ...formData, cpf: value });
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none transition-all"
                    placeholder="000.000.000-00"
                    required
                    maxLength={14}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Obrigatório para emissão de notas e recebimentos.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handlePasswordChange}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C]/20 focus:border-[#DC143C] outline-none transition-all text-base"
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  tabIndex={-1}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
              {formData.password && passwordErrors.length > 0 && (
                <div className="mt-2 text-xs space-y-1">
                  <p className="text-gray-500">A senha deve conter:</p>
                  {['Mínimo 8 caracteres', 'Uma letra maiúscula', 'Uma letra minúscula', 'Um número', 'Um caractere especial (!@#$%^&*)'].map((req, i) => (
                    <p key={i} className={passwordErrors.includes(req) ? 'text-red-500' : 'text-green-500'}>
                      {passwordErrors.includes(req) ? '✗' : '✓'} {req}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Senha</label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C]/20 focus:border-[#DC143C] outline-none transition-all text-base"
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="mt-1 text-xs text-red-500">As senhas não coincidem</p>
              )}
            </div>

            {/* Location for delivery persons */}
            {formData.userType === 'entregador' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Localização</label>
                <button
                  type="button"
                  onClick={getLocation}
                  disabled={gettingLocation}
                  className={`w-full py-3 px-4 border rounded-xl flex items-center justify-center gap-2 transition-all ${
                    location
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-[#DC143C]'
                  }`}
                >
                  <FiMapPin />
                  {gettingLocation
                    ? 'Obtendo localização...'
                    : location
                    ? '✓ Localização obtida!'
                    : 'Obter minha localização'}
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030] focus:ring-4 focus:ring-red-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Cadastrando...' : 'Criar Conta'}
            </button>

            <p className="text-xs text-gray-500 text-center">
              Ao criar uma conta, você concorda com nossos{' '}
              <Link to="/termos" className="text-[#DC143C] hover:underline">Termos de Uso</Link>
              {' '}e{' '}
              <Link to="/privacidade" className="text-[#DC143C] hover:underline">Política de Privacidade</Link>.
            </p>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Já tem conta?{' '}
              <Link to="/login" className="text-[#DC143C] font-semibold hover:underline">
                Entrar
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/50 text-sm mt-6">
          © 2025 BarueriFlash. Todos os direitos reservados.
        </p>
        <div className="text-center text-white/40 text-xs mt-2 space-x-4">
          <Link to="/termos" className="hover:text-white/60">Termos de Uso</Link>
          <Link to="/privacidade" className="hover:text-white/60">Privacidade</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
